import { useCallback, useEffect, useRef, useState } from 'react'
import { getMPDataWithParams } from 'src/mentor/services'
import {
  isCommonResponseSuccessfulV2,
  getDataMapFromCommonResponse,
} from 'src/mentor/helpers/utilityFunctions'
import { useStatsApi } from 'src/shared/stats/statsApiContext'

/**
 * Lazily fetches practitioner "My Reports" insights for ONE reportType.
 *
 * The request fires only when `enabled` (the owning sub-tab is active) and not yet fetched for the
 * current query key (reportType | discipline | from | to | clinicLocationId). Switching the date
 * range / discipline changes the key and triggers a single refetch; returning to an already-loaded
 * tab does NOT refetch. This is what keeps the Stats page from issuing any insights call until a
 * sub-tab is opened, and at most one call per (tab, range, discipline).
 */
export default function usePractitionerReportInsights({
  enabled,
  reportType,
  discipline,
  clinicBrandId,
  clinicLocationId,
  from,
  to,
}) {
  const [data, setData] = useState(null)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState(null)
  const fetchedKeyRef = useRef(null)

  // Portal override: the mentor portal hits the practitioner's own insights endpoint; the support
  // portal provides the admin endpoint + { mentorId } via StatsApiContext.
  const {
    practitionerInsightsPath,
    practitionerInsightsExtraParams,
    practitionerInsightsRequiresClinicLocation = true,
  } = useStatsApi()
  const extraParamsKey = practitionerInsightsExtraParams
    ? JSON.stringify(practitionerInsightsExtraParams)
    : ''

  const queryKey = [
    reportType,
    discipline || '',
    from || '',
    to || '',
    clinicLocationId || '',
    practitionerInsightsPath,
    extraParamsKey,
  ].join('|')

  const load = useCallback(async () => {
    if (!reportType || !from || !to) return
    // The practitioner's own endpoint requires a clinic location; admin surfaces opt out via
    // context and an absent location means "across all clinics".
    if (practitionerInsightsRequiresClinicLocation && !clinicLocationId) return
    setLoading(true)
    setError(null)
    try {
      const resp = await getMPDataWithParams(practitionerInsightsPath, {
        reportType,
        ...(clinicBrandId ? { clinicBrandId } : {}),
        ...(clinicLocationId ? { clinicLocationId } : {}),
        from,
        to,
        ...(discipline ? { discipline } : {}),
        ...(practitionerInsightsExtraParams || {}),
      })
      if (resp?.status === 200 && isCommonResponseSuccessfulV2(resp)) {
        setData(getDataMapFromCommonResponse(resp) || null)
      } else {
        setData(null)
        setError(resp?.data?.message || 'Could not load insights')
        fetchedKeyRef.current = null // allow a retry when the sub-tab is re-opened
      }
    } catch (e) {
      setData(null)
      setError('Could not load insights')
      fetchedKeyRef.current = null
    } finally {
      setLoading(false)
    }
  }, [reportType, discipline, clinicBrandId, clinicLocationId, from, to, practitionerInsightsPath, extraParamsKey, practitionerInsightsRequiresClinicLocation])

  useEffect(() => {
    if (!enabled) return
    if (fetchedKeyRef.current === queryKey) return
    fetchedKeyRef.current = queryKey
    load()
  }, [enabled, queryKey, load])

  return { data, loading, error, reload: load }
}
